/**
 * Challenge8KLanding — Reset Metabólico storytelling.
 *
 * Story: o utilizador chega inflamado → atravessa 3 fases (Inflamação→Equilíbrio,
 * Adaptação à Cetose, Optimização Metabólica) → emerge em cetose nutricional.
 *
 * Visual: hero image laboratorial (Gemini) animada com respiração, glow neon
 * verde, hue rotation, scan lines e partículas a orbitar (estilo Keto Fusion).
 */

import React, { useContext } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, Lock, Flame, Activity, Leaf, Sparkles, Zap } from "lucide-react";
import { AuthContext } from "../App";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_10364079-809d-4ab0-bd35-73b1a2bd39a9/artifacts/l9569gvx_Gemini_Generated_Image_1bfvlw1bfvlw1bfv.png";

const PHASES = [
  {
    num: 1,
    weeks: "Semanas 1-2",
    color: "#f97316",
    accent: "#fbbf24",
    title: "Inflamação → Equilíbrio",
    body: "Reduzir hidratos de forma gradual. Insulina baixa. Cortisol estabiliza. O fogo da inflamação começa a apagar.",
    icon: Flame,
  },
  {
    num: 2,
    weeks: "Semanas 3-4",
    color: "#84cc16",
    accent: "#bef264",
    title: "Adaptação à Cetose",
    body: "Switch metabólico instalado. Cetonas a circular. Foco mental constante e energia estável durante o dia inteiro.",
    icon: Activity,
  },
  {
    num: 3,
    weeks: "Semanas 5-8",
    color: "#00ff88",
    accent: "#22c55e",
    title: "Optimização Metabólica",
    body: "Cetose nutricional profunda. Queima máxima de gordura. Mitocôndria reconstruída. Esta é a tua nova base.",
    icon: Sparkles,
  },
];

const BENEFITS = [
  { icon: Flame, color: "#f97316", label: "Anti-Inflamatório" },
  { icon: Activity, color: "#84cc16", label: "Cetose Nutricional" },
  { icon: Zap, color: "#22c55e", label: "Energia Limpa" },
  { icon: Leaf, color: "#00ff88", label: "Foco Constante" },
];

export default function Challenge8KLanding() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const ctaLabel = !user ? "Criar conta para começar" : "Entrar no Desafio 8K";
  const goToProgram = () => navigate(user ? "/challenge-8k" : "/");

  return (
    <div className="min-h-screen bg-black text-zinc-100 relative overflow-x-hidden">
      {/* Cetose ambient pulses */}
      <div className="fixed top-0 left-0 w-1/2 h-screen pointer-events-none opacity-40">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-[140px] bg-lime-500/30 animate-pulse" />
      </div>
      <div className="fixed top-0 right-0 w-1/2 h-screen pointer-events-none opacity-40">
        <div className="absolute top-1/2 right-1/4 w-96 h-96 rounded-full blur-[140px] bg-emerald-400/30 animate-pulse" style={{ animationDelay: "1.5s" }} />
      </div>

      {/* HERO — Pure SVG energy explosion */}
      <section className="relative">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-0 items-center min-h-[88vh]">
          {/* Left — Hero image (DNA helix) with Keto Fusion-style dynamic effects */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="relative h-[60vh] lg:h-[88vh] overflow-hidden flex items-center justify-center"
            data-testid="c8k-hero-visual"
          >
            <motion.img
              src={HERO_IMG}
              alt="Desafio 8K — Hélice DNA"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: "drop-shadow(0 0 40px rgba(132,204,22,0.4))" }}
              animate={{
                scale: [1, 1.04, 1],
                filter: [
                  "drop-shadow(0 0 30px rgba(132,204,22,0.4)) drop-shadow(0 0 60px rgba(0,255,136,0.2)) hue-rotate(0deg)",
                  "drop-shadow(0 0 50px rgba(132,204,22,0.6)) drop-shadow(0 0 80px rgba(0,255,136,0.4)) hue-rotate(10deg)",
                  "drop-shadow(0 0 30px rgba(132,204,22,0.4)) drop-shadow(0 0 60px rgba(0,255,136,0.2)) hue-rotate(0deg)",
                ],
              }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Rotating energy ring overlay */}
            <motion.div
              className="absolute inset-8 rounded-full pointer-events-none"
              style={{
                background:
                  "conic-gradient(from 0deg, transparent 70%, rgba(132,204,22,0.3) 80%, rgba(0,255,136,0.4) 90%, transparent 100%)",
                filter: "blur(30px)",
              }}
              animate={{ rotate: 360 }}
              transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
            />

            {/* Counter-rotating particle ring */}
            <motion.div
              className="absolute inset-16 rounded-full border border-lime-500/20 pointer-events-none"
              animate={{ rotate: -360 }}
              transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
            >
              {[0, 60, 120, 180, 240, 300].map((deg, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 rounded-full"
                  style={{
                    background: i % 2 === 0 ? "#84cc16" : "#00ff88",
                    boxShadow: `0 0 10px ${i % 2 === 0 ? "#84cc16" : "#00ff88"}`,
                    top: "50%",
                    left: "50%",
                    transform: `rotate(${deg}deg) translateY(-180px)`,
                  }}
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 3, repeat: Infinity, delay: i * 0.4 }}
                />
              ))}
            </motion.div>

            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black pointer-events-none" />

            {/* Animated scan line */}
            <motion.div
              className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-lime-400 to-transparent pointer-events-none"
              animate={{ top: ["0%", "100%", "0%"] }}
              transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
            />
          </motion.div>

          {/* Right — Story */}
          <div className="relative px-6 lg:px-10 py-12 lg:py-0 z-10">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-900 border border-zinc-800 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[11px] uppercase tracking-widest text-zinc-400">Reset Metabólico · 8 Semanas · €99</span>
              </div>

              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.9] mb-2"
                style={{
                  color: "#84cc16",
                  textShadow: "0 0 30px rgba(132,204,22,0.6), 0 0 60px rgba(132,204,22,0.3)",
                }}
                data-testid="c8k-title-desafio"
              >
                DESAFIO
              </h1>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.9] mb-6"
                style={{
                  color: "#00ff88",
                  textShadow: "0 0 30px rgba(0,255,136,0.7), 0 0 60px rgba(0,255,136,0.4)",
                }}
                data-testid="c8k-title-8k"
              >
                8K
              </h1>

              <div className="h-0.5 w-full max-w-sm bg-gradient-to-r from-orange-500 via-lime-500 to-emerald-400 mb-4 rounded-full" />

              <p className="text-lg italic text-zinc-200 mb-2" data-testid="c8k-tagline">
                Da inflamação à cetose nutricional.
              </p>
              <p className="text-sm text-zinc-400 mb-8 max-w-md leading-relaxed">
                8 semanas. 3 fases. Uma jornada metabólica completa. O teu corpo aprende a queimar gordura como combustível primário e a inflamação dissipa-se.
              </p>

              <div className="grid grid-cols-4 gap-4 mb-8 max-w-md">
                {BENEFITS.map((b, i) => {
                  const Icon = b.icon;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + i * 0.1 }}
                      className="text-center"
                    >
                      <div
                        className="w-12 h-12 mx-auto rounded-xl border flex items-center justify-center mb-2 bg-zinc-950"
                        style={{ borderColor: `${b.color}55`, boxShadow: `0 0 20px ${b.color}30` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: b.color }} />
                      </div>
                      <p className="text-[10px] uppercase tracking-wider text-zinc-400 leading-tight">{b.label}</p>
                    </motion.div>
                  );
                })}
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={goToProgram}
                  className="bg-gradient-to-r from-lime-500 via-emerald-500 to-green-400 hover:from-lime-400 hover:via-emerald-400 hover:to-green-300 text-black font-bold text-base px-7 h-12 shadow-lg shadow-emerald-500/40"
                  data-testid="c8k-hero-cta"
                >
                  <Flame className="w-5 h-5 mr-2" /> {ctaLabel}
                </Button>
                <Button
                  onClick={() => document.getElementById("journey")?.scrollIntoView({ behavior: "smooth" })}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-900 hover:text-white h-12"
                >
                  Ver a jornada
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* JOURNEY — 3 fases storytelling */}
      <section id="journey" className="max-w-6xl mx-auto px-6 py-20 relative">
        <h2 className="text-3xl sm:text-5xl font-black mb-3 text-center tracking-tight">
          A <span style={{ color: "#f97316", textShadow: "0 0 15px rgba(249,115,22,0.4)" }}>inflamação</span> tem fim.<br />
          <span style={{ color: "#00ff88", textShadow: "0 0 20px rgba(0,255,136,0.5)" }}>A cetose nutricional</span> tem início.
        </h2>
        <p className="text-zinc-400 mb-16 text-center max-w-2xl mx-auto">
          Cada fase é uma transformação química. Spirit acompanha-te em cada semana.
        </p>

        {/* Phase gradient bar (inflammation → ketosis) */}
        <div className="max-w-4xl mx-auto mb-16 relative">
          <div className="h-2 rounded-full bg-gradient-to-r from-orange-600 via-lime-400 to-emerald-400 relative overflow-hidden">
            <motion.div
              className="absolute inset-0 bg-white/30"
              animate={{ x: ["-100%", "100%"] }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <div className="flex justify-between mt-3 text-[11px] uppercase tracking-widest">
            <span className="text-orange-400">🔥 Inflamação</span>
            <span className="text-lime-400">🌀 Adaptação</span>
            <span className="text-emerald-400">💚 Cetose</span>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {PHASES.map((p, idx) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
                whileHover={{ y: -6 }}
                className="relative p-6 rounded-2xl border shadow-2xl cursor-default overflow-hidden bg-zinc-950"
                style={{ borderColor: `${p.color}40`, boxShadow: `0 12px 40px ${p.color}25` }}
                data-testid={`c8k-phase-${p.num}`}
              >
                {/* Background glow */}
                <motion.div
                  className="absolute -top-16 -right-16 w-48 h-48 rounded-full"
                  style={{ background: p.color, filter: "blur(60px)", opacity: 0.3 }}
                  animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2] }}
                  transition={{ duration: 5, repeat: Infinity, delay: idx * 0.7 }}
                />
                {/* Icon glow */}
                <div className="relative mb-4">
                  <motion.div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center border-2"
                    style={{ borderColor: p.color, background: `${p.color}15`, boxShadow: `0 0 25px ${p.color}55` }}
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 3, repeat: Infinity, delay: idx * 0.5 }}
                  >
                    <Icon className="w-7 h-7" style={{ color: p.accent }} />
                  </motion.div>
                </div>
                <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: p.color }}>
                  Fase {p.num} · {p.weeks}
                </p>
                <h3 className="text-2xl font-bold mb-3 text-zinc-100 tracking-tight">{p.title}</h3>
                <p className="text-sm text-zinc-300 leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-3xl mx-auto px-6 py-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative rounded-3xl bg-zinc-950 border border-zinc-800 p-8 text-center overflow-hidden"
          data-testid="c8k-pricing-card"
        >
          <motion.div
            className="absolute inset-0 rounded-3xl opacity-60 pointer-events-none"
            style={{
              background: "linear-gradient(45deg, transparent 30%, #84cc16, transparent 50%, #00ff88, transparent 70%)",
              backgroundSize: "200% 200%",
            }}
            animate={{ backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
          <div className="relative bg-zinc-950 rounded-3xl p-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-900 border border-zinc-800 mb-5">
              <Zap className="w-3 h-3 text-emerald-400" />
              <span className="text-[10px] uppercase tracking-widest text-zinc-400">Reset Metabólico Completo</span>
            </div>
            <h3 className="text-5xl font-black mb-1">
              <span style={{ color: "#00ff88", textShadow: "0 0 25px rgba(0,255,136,0.6)" }}>€99</span>
            </h3>
            <p className="text-xs text-zinc-500 mb-1">ou 2× €59 sem juros</p>
            <p className="text-sm text-zinc-400 mb-6">8 semanas de acompanhamento total · Spirit IA · Body Twin incluído.</p>
            <ul className="text-left max-w-md mx-auto space-y-2 mb-6">
              {[
                "Plano cetogénico estrito personalizado",
                "3 fases progressivas (inflamação → cetose nutricional)",
                "Protocolo de jejum periodizado (0h → 12h → 16h → 18h)",
                "Spirit IA com missões diárias contextuais",
                "Body Twin Premium para visualizar a transformação",
              ].map((line, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: ["#84cc16","#22c55e","#10b981","#00ff88","#84cc16"][i] }} />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <Button
              onClick={goToProgram}
              className="bg-gradient-to-r from-lime-500 via-emerald-500 to-green-400 hover:from-lime-400 hover:via-emerald-400 hover:to-green-300 text-black font-bold text-base px-8 h-12 shadow-lg shadow-emerald-500/40"
              data-testid="c8k-pricing-cta"
            >
              {!user && <Lock className="w-4 h-4 mr-2" />}
              {ctaLabel}
            </Button>
            {!user && <p className="text-xs text-zinc-500 mt-3">Disponível após criar conta no Keto Lab.</p>}
          </div>
        </motion.div>
      </section>
    </div>
  );
}
